import React, { useRef, type PointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  clampPairColumnWidths,
  columnAvailableWidth,
} from '../domain/column-presets';
import { resolveRowColumnMinWidthPercents } from '../domain/column-content-min';
import {
  beginHistoryInteraction,
  endHistoryInteraction,
} from '../domain/document-lifecycle';
import { computeEmailBuilderLayoutColumnResizeWidths } from '../editing/emailBuilderLayoutColumnResize';
import { findRow } from '../domain/find';
import { getEditorStore, getEditorStoreState } from '../domain/store';

type ColumnResizeHandleProps = {
  rowId: string;
  leftIndex: number;
  left: string;
  leftPercent: number;
  rightPercent: number;
  innerWidth: number;
  gap: number;
  rowActive: boolean;
  layoutActive: boolean;
  onDarkSurface?: boolean;
  onSelect: () => void;
};

export function ColumnResizeHandle({
  rowId,
  leftIndex,
  left,
  leftPercent,
  rightPercent,
  innerWidth,
  gap,
  rowActive,
  layoutActive,
  onDarkSurface = false,
  onSelect,
}: ColumnResizeHandleProps) {
  const { t } = useTranslation();
  const dragging = useRef(false);
  const startX = useRef(0);
  const startWidths = useRef<number[]>([]);
  const startMinPercents = useRef({ left: 1, right: 1 });

  function finishDrag(event: PointerEvent<HTMLDivElement>) {
    if (!dragging.current) {
      return;
    }
    dragging.current = false;
    endHistoryInteraction();
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    event.currentTarget.dataset.dragging = 'false';
  }

  function onPointerDown(event: PointerEvent<HTMLDivElement>) {
    event.preventDefault();
    event.stopPropagation();

    const state = getEditorStoreState();
    const row = findRow(state.document, rowId);
    if (!row) {
      return;
    }

    dragging.current = true;
    startX.current = event.clientX;
    startWidths.current = [...row.columnWidths];
    const minPercents = resolveRowColumnMinWidthPercents(
      row,
      innerWidth,
      state.document.settings.bodyFontFamily,
    );
    startMinPercents.current = {
      left: minPercents[leftIndex] ?? 1,
      right: minPercents[leftIndex + 1] ?? 1,
    };
    beginHistoryInteraction();
    event.currentTarget.setPointerCapture(event.pointerId);
    event.currentTarget.dataset.dragging = 'true';
    onSelect();
  }

  function onPointerMove(event: PointerEvent<HTMLDivElement>) {
    if (!dragging.current) {
      return;
    }
    const available = columnAvailableWidth(
      innerWidth,
      gap,
      startWidths.current.length,
    );
    if (available <= 0) {
      return;
    }

    const delta = event.clientX - startX.current;
    const deltaPercent = Math.round((delta / available) * 100);
    let next = computeEmailBuilderLayoutColumnResizeWidths({
      widths: startWidths.current,
      dividerIndex: leftIndex,
      deltaPercent,
      shiftKey: event.shiftKey,
      altKey: event.altKey,
    });
    next = clampPairColumnWidths(
      next,
      leftIndex,
      startMinPercents.current.left,
      startMinPercents.current.right,
    );

    const row = findRow(getEditorStoreState().document, rowId);
    if (!row) {
      return;
    }
    if (
      row.columnWidths[leftIndex] === next[leftIndex] &&
      row.columnWidths[leftIndex + 1] === next[leftIndex + 1]
    ) {
      return;
    }
    getEditorStore().setColumnWidths(rowId, next);
  }

  return (
    <div
      className={'email-builder-column-resize-handle'}
      style={{ left }}
      data-row-active={rowActive ? 'true' : 'false'}
      data-layout-active={layoutActive ? 'true' : 'false'}
      data-on-dark={onDarkSurface ? 'true' : 'false'}
      tabIndex={-1}
      role="separator"
      aria-orientation="vertical"
      aria-label={t('Resize columns')}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={finishDrag}
      onPointerCancel={finishDrag}
      onMouseDown={event => event.stopPropagation()}
      onClick={event => event.stopPropagation()}>
      <span className={'email-builder-column-resize-bar'} />
      <span className={'email-builder-column-resize-sizes'}>
        {leftPercent}% / {rightPercent}%
      </span>
    </div>
  );
}
